// soccer_admin/src/pages/Users.jsx
import React, { useEffect, useState } from 'react';
import { MagnifyingGlassIcon, TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { fetchAdminUsers, deleteUser as deleteUserApi } from '../services/api';

const getRemaining = (played) => Math.max(0, 5 - (played || 0));

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const loadUsers = async (currentPage = page) => {
    setLoading(true);
    try {
      const res = await fetchAdminUsers(currentPage, limit);
      const usersData = res.data.data || res.data;
      setUsers(usersData);
      setFilteredUsers(usersData);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.pages || 1);
        setTotalUsers(res.data.pagination.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(page);
  }, [page]);

  // Filter on search term change
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }
    const lowerTerm = searchTerm.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.username.toLowerCase().includes(lowerTerm) ||
        user.email.toLowerCase().includes(lowerTerm)
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`⚠️ Are you sure you want to delete user "${username}" (ID: ${userId})?\n\nThis action cannot be undone and will delete all related data (cards, matches, standings, etc.).`)) {
      return;
    }
    setDeletingId(userId);
    try {
      await deleteUserApi(userId);
      await loadUsers(page);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  if (loading) return <div className="text-white text-center py-10">Loading users...</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-white">Users Management</h1>
        <div className="relative w-full sm:w-64">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by username or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-800 text-white border border-gray-600 focus:outline-none focus:border-yellow-500"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto bg-gray-800 rounded-lg">
        <table className="min-w-full text-white">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="p-3">ID</th>
              <th className="p-3">Username</th>
              <th className="p-3">Email</th>
              <th className="p-3">Continent</th>
              <th className="p-3">Total Points</th>
              <th className="p-3 text-center">Matches Left</th>
              <th className="p-3">Role</th>
              <th className="p-3">Joined</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(filteredUsers) && filteredUsers.map((user) => {
              const domesticRemaining = getRemaining(user.domestic_matches_today);
              const championsRemaining = getRemaining(user.champions_matches_today);
              const europaRemaining = getRemaining(user.europa_matches_today);
              return (
                <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                  <td className="p-3 text-white">{user.id}</td>
                  <td className="p-3 font-medium text-white">{user.username}</td>
                  <td className="p-3 text-white">{user.email}</td>
                  <td className="p-3 text-white">{user.continent}</td>
                  <td className="p-3 text-yellow-400 font-bold">{user.total_points || 0}</td>
                  <td className="p-3">
                    <div className="flex justify-center items-center gap-3">
                      <span className="flex items-center gap-1 text-white" title="Domestic League">🏠 {domesticRemaining}</span>
                      <span className="flex items-center gap-1 text-white" title="Champions League">🌍 {championsRemaining}</span>
                      <span className="flex items-center gap-1 text-white" title="Europa League">🇪🇺 {europaRemaining}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.role === 'admin' ? 'bg-yellow-600 text-black' : 'bg-gray-600 text-white'}`}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td className="p-3 text-gray-300">{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="p-3">
                    <button
                      onClick={() => handleDelete(user.id, user.username)}
                      disabled={deletingId === user.id}
                      className="text-red-400 hover:text-red-300 transition disabled:opacity-50"
                      title="Delete user"
                    >
                      {deletingId === user.id ? (
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                      ) : (
                        <TrashIcon className="h-5 w-5" />
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!Array.isArray(filteredUsers) || filteredUsers.length === 0) && (
          <div className="text-center text-gray-400 py-8">No users found.</div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden grid grid-cols-1 gap-4">
        {Array.isArray(filteredUsers) && filteredUsers.map((user) => {
          const domesticRemaining = getRemaining(user.domestic_matches_today);
          const championsRemaining = getRemaining(user.champions_matches_today);
          const europaRemaining = getRemaining(user.europa_matches_today);
          return (
            <div key={user.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700 shadow-lg relative">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-lg font-bold text-white">{user.username}</p>
                  <p className="text-sm text-gray-300">{user.email}</p>
                </div>
                <button
                  onClick={() => handleDelete(user.id, user.username)}
                  disabled={deletingId === user.id}
                  className="text-red-400 hover:text-red-300 transition disabled:opacity-50"
                >
                  {deletingId === user.id ? (
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                  ) : (
                    <TrashIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-300">ID:</span> <span className="text-white">{user.id}</span></div>
                <div><span className="text-gray-300">Continent:</span> <span className="text-white">{user.continent}</span></div>
                <div><span className="text-gray-300">Points:</span> <span className="text-yellow-400 font-bold">{user.total_points || 0}</span></div>
                <div><span className="text-gray-300">Joined:</span> <span className="text-white">{new Date(user.created_at).toLocaleDateString()}</span></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                <span className="flex items-center gap-1 text-white">🏠 {domesticRemaining}</span>
                <span className="flex items-center gap-1 text-white">🌍 {championsRemaining}</span>
                <span className="flex items-center gap-1 text-white">🇪🇺 {europaRemaining}</span>
              </div>
            </div>
          );
        })}
        {(!Array.isArray(filteredUsers) || filteredUsers.length === 0) && (
          <div className="text-center text-white py-8">No users found.</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6 text-white">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-sm">
            Page {page} of {totalPages} ({totalUsers} users)
          </span>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Users;